import { useEffect, useState } from "react";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";

const STATUSES = ["NUOVA", "IN_LAVORAZIONE", "IN_ATTESA_CLIENTE", "RISOLTA", "CHIUSA"];
const BADGE = {
  NUOVA: "bg-sky-500/20 text-sky-300", IN_LAVORAZIONE: "bg-amber-500/20 text-amber-300",
  IN_ATTESA_CLIENTE: "bg-purple-500/20 text-purple-300", RISOLTA: "bg-emerald-500/20 text-emerald-300",
  CHIUSA: "bg-slate-500/20 text-slate-300",
};

export default function OwnerSupport() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("");

  const load = () => http.get(`/support/tickets${filter ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data.tickets || [])).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [filter]);

  const open = (id) => http.get(`/support/tickets/${id}`).then((r) => setActive(r.data)).catch((e) => toast.error(apiErr(e)));
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await http.post(`/support/tickets/${active.id}/messages`, { message: reply }); setReply(""); open(active.id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const update = async (patch) => {
    try { const { data } = await http.put(`/support/tickets/${active.id}`, patch); setActive(data); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (active) {
    return (
      <div data-testid="owner-ticket-detail" className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <button onClick={() => { setActive(null); load(); }} className="flex items-center gap-1 text-sm text-slate-400 mb-4"><ArrowLeft className="w-4 h-4" /> Tutte le richieste</button>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h2 className="font-heading text-xl font-bold">{active.subject}</h2>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${BADGE[active.status]}`}>{active.status}</span>
        </div>
        <div className="text-xs text-slate-400 mb-4">{active.tenant_name} · {active.created_by_name} · {active.category} · {active.priority}</div>
        <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
          {active.messages.map((m) => (
            <div key={m.id} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.author_role === "OWNER" ? "ml-auto bg-sky-600 text-white" : m.author_role === "ASSISTANT_CONTEXT" ? "bg-white/5 border border-white/10 text-slate-400 italic" : "bg-white/10 text-slate-100"}`}>
              <div className="text-[10px] opacity-70 mb-0.5">{m.author_name}</div>{m.text}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Input data-testid="owner-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Rispondi al cliente…" className="bg-white/5 border-white/10 text-slate-100" />
          <Button data-testid="owner-reply-send" onClick={sendReply}><Send className="w-4 h-4" /></Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="owner-ticket-status" value={active.status} onChange={(e) => update({ status: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0B1120]">{s}</option>)}
          </select>
          <select data-testid="owner-ticket-priority" value={active.priority} onChange={(e) => update({ priority: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
            <option value="normale" className="bg-[#0B1120]">normale</option>
            <option value="urgente" className="bg-[#0B1120]">urgente</option>
          </select>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="owner-support">
      <div className="flex flex-wrap gap-2 mb-4">
        {["", ...STATUSES].map((s) => (
          <button key={s || "all"} data-testid={`owner-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${filter === s ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            {s || "TUTTE"}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {tickets.length === 0 && <div className="text-slate-500 text-sm text-center py-10">Nessuna richiesta</div>}
        {tickets.map((tk) => (
          <button key={tk.id} data-testid={`owner-ticket-${tk.id}`} onClick={() => open(tk.id)}
            className="w-full flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:border-sky-400/40 text-left">
            <div className="min-w-0">
              <div className="font-semibold truncate flex items-center gap-2">{tk.subject}{tk.unread_owner && <span className="w-2 h-2 rounded-full bg-sky-400" />}</div>
              <div className="text-xs text-slate-400">{tk.tenant_name} · {tk.category} · {tk.priority}</div>
            </div>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${BADGE[tk.status]}`}>{tk.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
